import { useId, useRef, type ReactNode } from "react";
import clsx from "clsx";
import { Button } from "../Button/Button.js";
import { useModalA11y } from "../hooks/useModalA11y.js";
import styles from "./ActionModal.module.scss";

/** `sm` 420 for a one- or two-control form; `md` 480 where the body needs the room. */
export type ActionModalSize = "sm" | "md";
export type ActionModalTone = "default" | "danger";

/** One choice button in a multi-option modal (see {@link ActionModalProps.actions}). */
export interface ActionModalAction {
  /** Button text. */
  label: string;
  onClick: () => void;
  /** `danger` renders it as the destructive (red) choice. */
  tone?: ActionModalTone;
  /** Render it as the primary (filled) button — the commit in a Cancel / Remove / Save row. */
  primary?: boolean;
  /** Disable this one button (an incomplete form) without disabling the others. */
  disabled?: boolean;
  /** While true this button shows a spinner and every action + Cancel is disabled. */
  loading?: boolean;
}

export interface ActionModalProps {
  /** Accessible dialog title, rendered as the heading unless `hideHeader`. */
  title: string;
  /** The form / message. Optional — a pure chooser (see {@link actions}) has none. */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** The single-confirm handler. Unused (and optional) when {@link actions} is given. */
  onConfirm?: () => void;
  /** Also the dismiss handler — the scrim and Escape both route here, as does Cancel. */
  onCancel: () => void;
  /** While true the confirm shows a spinner and both actions are disabled. */
  loading?: boolean;
  /** Independently disable the confirm (an incomplete form), without disabling Cancel. */
  confirmDisabled?: boolean;
  /** `danger` renders the confirm as the destructive (red) button. */
  confirmTone?: ActionModalTone;
  /** Replace the single Confirm with a set of choice buttons that STACK full-width (Cancel still shown at the
   *  foot) — a small chooser, e.g. "Back out as me" / "Back out as the band". Stacked rather than sharing the
   *  row because a choice label is a sentence, not a verb. `onConfirm`/`confirmLabel`/`confirmTone` are unused. */
  actions?: ActionModalAction[];
  size?: ActionModalSize;
  /** Drop the visible heading; the title stays as an sr-only label so `aria-labelledby` still resolves. */
  hideHeader?: boolean;
  /** An ALERT: no Cancel button — the confirm alone fills the footer ("OK"). The scrim and Escape still dismiss
   *  through `onCancel`. For a refusal the reader can only acknowledge. Single-confirm mode only. */
  hideCancel?: boolean;
}

/**
 * THE ACTION MODAL — a short form behind a decision (PO, 2026-09-11): "cancel left, confirm right, buttons
 * that add up to take full width. slim footer and less padding."
 *
 * It is the fourth member of the overlay family and it exists because the other three each answer a
 * different question. `Modal` is a full working surface — its body inset and right-aligned footer are sized
 * for a form with sections, and at 420px that inset reads as a frame rather than a margin. `AcDialog` is a
 * CONFIRM: a sentence and a decision, with no fields to fill. `Sheet` is the mobile bottom panel. This one
 * is the gap between them — two or three controls and a commitment.
 *
 * **The footer is the component.** Cancel and confirm SPLIT the row rather than hugging their text at the
 * right edge, which is what makes a small card read as a decision with two equal outcomes instead of a page
 * with a button on it. It is `flex: 1` on the children rather than a hard 50%, so the third action this is
 * eventually meant to grow simply shares the row when it arrives — nothing here is written against there
 * being exactly two.
 *
 * At `--ac-z-dialog`, like `AcDialog`: its job is to open over something, and `useModalA11y`'s
 * topmost-only Escape unwinds it before whatever it opened over.
 */
export function ActionModal({
  title,
  children,
  confirmLabel = "Save",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  loading = false,
  confirmDisabled = false,
  confirmTone = "default",
  actions,
  size = "sm",
  hideHeader = false,
  hideCancel = false,
}: ActionModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  useModalA11y(dialogRef, onCancel);
  const busy = loading || (actions?.some((a) => a.loading) ?? false);

  return (
    <div className={styles.backdrop}>
      <button
        type="button"
        className={styles.scrim}
        aria-label={cancelLabel}
        tabIndex={-1}
        onClick={onCancel}
      />
      <div
        ref={dialogRef}
        className={styles.dialog}
        data-size={size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        tabIndex={-1}
      >
        {hideHeader ? (
          <h2 id={labelId} className={styles.srOnly}>
            {title}
          </h2>
        ) : (
          <header className={styles.head}>
            <h2 id={labelId} className={styles.title}>
              {title}
            </h2>
          </header>
        )}

        {children != null && <div className={styles.body}>{children}</div>}

        {/* `data-action-foot` so a test can pin WHERE a control sits without reaching for a CSS-module class
            — vitest runs with `css: false`, so a class name is the one thing a test cannot see (the same
            reason `Modal`'s footer carries `data-modal-foot`). */}
        <div className={styles.foot} data-action-foot>
          {actions ? (
            // A CHOOSER: Cancel leads (left), then each choice, all sharing the row full-width.
            <>
              <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
                {cancelLabel}
              </Button>
              {actions.map((a, i) => (
                <Button
                  key={i}
                  type="button"
                  variant={a.primary ? "primary" : "ghost"}
                  className={clsx(a.tone === "danger" && styles.dangerAction)}
                  loading={a.loading}
                  disabled={(busy && !a.loading) || a.disabled}
                  onClick={a.onClick}
                >
                  {a.label}
                </Button>
              ))}
            </>
          ) : (
            <>
              {!hideCancel && (
                <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
                  {cancelLabel}
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                className={clsx(confirmTone === "danger" && styles.dangerAction)}
                loading={loading}
                disabled={confirmDisabled}
                onClick={onConfirm}
              >
                {confirmLabel}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
